// FILE: src/pages/AuditLogPage.jsx
//
// Audit Logs — UI redesign (visual layer only).
//
// What changed: presentation only. Scoped CSS under `.alpg-root`
// (Geist / Geist Mono, sticky header + filter bar, chip-styled action
// types, card table, skinned detail dialog).
//
// What is preserved byte-for-byte in behavior:
//   - API.get("/audit-logs", { params }) with page + 1 (server is 1-based),
//     limit, and conditionally-spread filters.
//   - Filter state semantics (raw YYYY-MM-DD dates, the exact actionType
//     option list, free-text targetEntity).
//   - MUI TablePagination with 0-based page state, rowsPerPageOptions
//     [5, 10, 25], and the same label props.
//   - handleSearch / handleClearFilters keep their existing
//     setPage(0) + fetchLogs() flow (known double-fetch quirk preserved).
//   - Row click -> detail modal with ReactDiffViewer
//     (react-diff-viewer-continued), DiffMethod.WORDS, and the
//     safeObject + JSON.stringify usage.

import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import API from "../api";

// NEW: Import ReactDiffViewer from react-diff-viewer-continued
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";

// Helper to ensure we never pass null/undefined to JSON.stringify
function safeObject(obj) {
  return obj && typeof obj === "object" ? obj : {};
}

// ----------------------------------------
// Presentation helpers (no data-flow changes)
// ----------------------------------------
function logUsername(log) {
  return log.user && log.user.username
    ? log.user.username
    : log.username || "N/A";
}

function logTimestamp(log) {
  return log.timestamp
    ? new Date(log.timestamp).toLocaleString()
    : log.createdAt
    ? new Date(log.createdAt).toLocaleString()
    : "";
}

// Chip variant: *_FAIL / *_FAILED -> danger, auth events -> primary,
// everything else -> neutral. Pure presentation.
function actionChipVariant(actionType) {
  if (!actionType) return "neutral";
  const t = String(actionType).toUpperCase();
  if (t.endsWith("_FAIL") || t.endsWith("_FAILED")) return "danger";
  if (t === "LOGIN" || t === "REGISTER" || t === "VERIFY") return "primary";
  return "neutral";
}

// ----------------------------------------
// CSS — fully scoped under `.alpg-root`.
// ----------------------------------------
const AUDIT_LOG_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* Dialog papers reuse .alpg-root for tokens — never inherit page sizing */
.MuiPaper-root.alpg-root {
  min-height: 0;
  background: #fff;
}
.alpg-root {
  --alpg-primary: #1E90FF;
  --alpg-primary-soft: #E8F3FF;
  --alpg-warn: #FFC107;
  --alpg-warn-soft: #FFF7DC;
  --alpg-warn-deep: #B86E00;
  --alpg-success: #2E7D32;
  --alpg-success-soft: #E8F5E9;
  --alpg-danger: #C62828;
  --alpg-danger-soft: #FDECEC;
  --alpg-text-1: #111827;
  --alpg-text-2: #374151;
  --alpg-text-3: #6B7280;
  --alpg-text-4: #9CA3AF;
  --alpg-border-1: #ECECEC;
  --alpg-border-2: #DDDDDD;
  --alpg-surface: #FFFFFF;
  --alpg-surface-sub: #FAFAFA;
  --alpg-surface-sub-2: #F4F4F5;
  --alpg-radius-sm: 6px;
  --alpg-radius-md: 8px;
  --alpg-radius-lg: 12px;
  --alpg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --alpg-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --alpg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--alpg-text-1);
  background: var(--alpg-surface-sub);
  min-height: 100vh;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.alpg-root * { box-sizing: border-box; }

/* ----- Sticky top region (page header + filter bar) ----- */
.alpg-sticky-top {
  position: sticky;
  top: var(--alpg-navbar-h);
  z-index: 30;
  background: var(--alpg-surface-sub);
  border-bottom: 1px solid var(--alpg-border-1);
}
.alpg-header {
  padding: 20px 24px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.alpg-title-block { min-width: 0; flex: 1 1 320px; }
.alpg-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  color: var(--alpg-text-1);
}
.alpg-subtitle {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px; color: var(--alpg-text-3); flex-wrap: wrap;
}
.alpg-subtitle-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--alpg-text-4); }
.alpg-count-pill {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--alpg-primary-soft);
  color: #0E5EAB;
  padding: 3px 9px; border-radius: 999px;
  font-size: 11.5px; font-weight: 500;
  border: 1px solid #BBDCFB;
}
.alpg-count-pill b {
  font-weight: 600;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
}

/* ----- Filter bar ----- */
.alpg-filterbar {
  display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
  padding: 14px 24px 16px;
  background: var(--alpg-surface);
  border-top: 1px solid var(--alpg-border-1);
}
.alpg-filter-field { display: flex; flex-direction: column; gap: 4px; }
.alpg-filter-field label {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--alpg-text-3);
}
.alpg-input, .alpg-select {
  height: 34px; padding: 0 10px;
  background: var(--alpg-surface);
  border: 1px solid var(--alpg-border-2);
  border-radius: var(--alpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--alpg-text-1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  min-width: 140px;
}
.alpg-input:focus, .alpg-select:focus {
  outline: none;
  border-color: var(--alpg-primary);
  box-shadow: 0 0 0 3px var(--alpg-primary-soft);
}
.alpg-input[type="date"] {
  font-family: 'Geist Mono', monospace;
  font-size: 12.5px;
  min-width: 140px;
}
.alpg-input-search {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg' fill='none'%3E%3Ccircle cx='7' cy='7' r='4.5' stroke='%236B7280' stroke-width='1.4'/%3E%3Cpath d='M10.5 10.5l3 3' stroke='%236B7280' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px center;
  padding-left: 30px;
  min-width: 180px;
}
.alpg-filter-actions { display: flex; align-items: center; gap: 6px; }

/* ----- Buttons ----- */
.alpg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--alpg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.alpg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.alpg-btn-primary { background: var(--alpg-primary); color: #fff; }
.alpg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.alpg-btn-ghost {
  background: var(--alpg-surface); color: var(--alpg-text-2);
  border-color: var(--alpg-border-2);
}
.alpg-btn-ghost:hover:not(:disabled) { background: var(--alpg-surface-sub-2); }

/* ----- Body region ----- */
.alpg-body { padding: 18px 24px 32px; }
.alpg-alert {
  margin-bottom: 14px;
  padding: 10px 14px;
  background: var(--alpg-danger-soft);
  color: var(--alpg-danger);
  border-left: 3px solid var(--alpg-danger);
  border-radius: var(--alpg-radius-sm);
  font-size: 13px;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.alpg-alert button {
  background: none; border: none; color: inherit; cursor: pointer;
  font-size: 16px; line-height: 1; padding: 0 4px;
}

/* ----- Table card ----- */
.alpg-card {
  background: var(--alpg-surface);
  border: 1px solid var(--alpg-border-1);
  border-radius: var(--alpg-radius-lg);
  box-shadow: var(--alpg-shadow-sm);
  overflow: hidden;
}
.alpg-table-wrap { width: 100%; overflow-x: auto; }
.alpg-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  min-width: 860px;
}
.alpg-table thead th {
  background: var(--alpg-surface);
  color: var(--alpg-text-2);
  font-size: 11.5px; font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px;
  border-bottom: 1px solid var(--alpg-border-2);
  text-align: left;
  white-space: nowrap;
}
.alpg-table tbody td {
  padding: 11px 10px;
  border-bottom: 1px solid var(--alpg-border-1);
  vertical-align: middle;
  color: var(--alpg-text-1);
}
.alpg-table tbody tr:last-child td { border-bottom: none; }
.alpg-table tbody tr {
  cursor: pointer;
  transition: background 0.1s ease;
}
.alpg-table tbody tr:hover { background: var(--alpg-surface-sub); }
.alpg-table tbody td.mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
}
.alpg-user-cell { font-weight: 500; font-size: 12.5px; color: var(--alpg-text-1); }
.alpg-muted { color: var(--alpg-text-4); }
.alpg-view-hint {
  font-size: 11px; color: var(--alpg-text-4);
  opacity: 0;
  transition: opacity 0.12s ease;
  white-space: nowrap;
}
.alpg-table tbody tr:hover .alpg-view-hint { opacity: 1; }

/* ----- Action type chips ----- */
.alpg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px 2px 6px;
  font-family: 'Geist Mono', monospace;
  font-size: 10.5px; font-weight: 500;
  letter-spacing: 0.02em;
  border-radius: 999px;
  white-space: nowrap;
}
.alpg-chip-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.alpg-chip-primary {
  background: var(--alpg-primary-soft);
  color: #0E5EAB;
  border: 1px solid #BBDCFB;
}
.alpg-chip-primary .alpg-chip-dot { background: var(--alpg-primary); }
.alpg-chip-danger {
  background: var(--alpg-danger-soft);
  color: var(--alpg-danger);
  border: 1px solid #F4C7C7;
}
.alpg-chip-danger .alpg-chip-dot { background: var(--alpg-danger); }
.alpg-chip-neutral {
  background: var(--alpg-surface-sub-2);
  color: var(--alpg-text-2);
  border: 1px solid var(--alpg-border-2);
}
.alpg-chip-neutral .alpg-chip-dot { background: var(--alpg-text-4); }

/* ----- Empty / Loading states ----- */
.alpg-state {
  padding: 56px 32px;
  text-align: center;
  color: var(--alpg-text-3);
}
.alpg-state-icon {
  margin: 0 auto 12px;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--alpg-surface-sub-2);
  color: var(--alpg-text-3);
}
.alpg-state-title { color: var(--alpg-text-2); font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.alpg-state-sub { font-size: 12.5px; color: var(--alpg-text-3); max-width: 360px; margin: 0 auto; }
.alpg-state-loading {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}

/* ----- Pagination wrapper (skins MUI TablePagination) ----- */
.alpg-pagination-wrap {
  border-top: 1px solid var(--alpg-border-1);
  background: var(--alpg-surface);
}
.alpg-root .MuiTablePagination-root {
  color: var(--alpg-text-2);
  font-family: 'Geist', sans-serif;
  overflow: visible;
}
.alpg-root .MuiTablePagination-toolbar { min-height: 46px; padding-right: 8px; }
.alpg-root .MuiTablePagination-selectLabel {
  font-family: 'Geist', sans-serif;
  font-size: 12px; color: var(--alpg-text-3); margin: 0;
}
.alpg-root .MuiTablePagination-displayedRows {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12px; color: var(--alpg-text-2); margin: 0;
}
.alpg-root .MuiTablePagination-select {
  font-family: 'Geist Mono', monospace;
  font-size: 12px;
}

/* ----- Modal skinning (MUI Dialog underneath, custom look) ----- */
.alpg-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: var(--alpg-radius-lg) !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  overflow: hidden;
}
.alpg-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--alpg-border-1);
}
.alpg-dlg-title { font-size: 16px; font-weight: 600; color: var(--alpg-text-1); margin: 0; }
.alpg-dlg-sub { font-size: 12px; color: var(--alpg-text-3); margin-top: 3px; }
.alpg-dlg-close {
  background: none; border: none; color: var(--alpg-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.alpg-dlg-close:hover { background: var(--alpg-surface-sub-2); color: var(--alpg-text-1); }
.alpg-dlg-body { padding: 18px 20px; }
.alpg-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--alpg-border-1);
  background: var(--alpg-surface-sub);
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px;
}
.alpg-dlg-foot-l { font-size: 12px; color: var(--alpg-text-3); }
.alpg-dlg-foot-r { display: flex; gap: 8px; }

/* Detail fields inside the dialog */
.alpg-detail-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  padding: 14px 16px;
  background: var(--alpg-surface-sub);
  border: 1px solid var(--alpg-border-1);
  border-radius: var(--alpg-radius-md);
  margin-bottom: 16px;
}
@media (max-width: 600px) { .alpg-detail-grid { grid-template-columns: 1fr 1fr; } }
.alpg-detail-field-lab {
  font-size: 10.5px; text-transform: uppercase; color: var(--alpg-text-3);
  letter-spacing: 0.04em; margin-bottom: 2px;
}
.alpg-detail-field-val {
  font-size: 13px; font-weight: 500; color: var(--alpg-text-1);
  word-break: break-word;
}
.alpg-detail-field-val.mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
}
.alpg-detail-sec-head {
  font-size: 11.5px; font-weight: 600;
  color: var(--alpg-text-2); text-transform: uppercase; letter-spacing: 0.04em;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--alpg-border-1);
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.alpg-detail-sec-hint {
  font-size: 10.5px; font-weight: 400; color: var(--alpg-text-4);
  text-transform: none; letter-spacing: 0;
}
.alpg-diff-wrap {
  border: 1px solid var(--alpg-border-1);
  border-radius: var(--alpg-radius-md);
  overflow-x: auto;
}
.alpg-error-sec { margin-top: 16px; }
.alpg-error-pre {
  background: var(--alpg-danger-soft);
  border: 1px solid #F4C7C7;
  border-left: 3px solid var(--alpg-danger);
  color: var(--alpg-danger);
  padding: 10px 12px;
  border-radius: var(--alpg-radius-sm);
  font-family: 'Geist Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  margin: 0;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}
`;

// ----------------------------------------
// Small inline icons
// ----------------------------------------
function IconClose({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconSearch({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10.5 10.5l3 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
function IconShield({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M10 2.2l6 2.2v4.4c0 3.9-2.6 7.3-6 8.6-3.4-1.3-6-4.7-6-8.6V4.4l6-2.2z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M7.4 9.8l1.8 1.8 3.4-3.6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Action type chip (presentation only)
function ActionChip({ actionType }) {
  const variant = actionChipVariant(actionType);
  return (
    <span className={`alpg-chip alpg-chip-${variant}`}>
      <span className="alpg-chip-dot" />
      {actionType || "N/A"}
    </span>
  );
}

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [actionType, setActionType] = useState("");
  const [targetEntity, setTargetEntity] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // State for modal view
  const [selectedLog, setSelectedLog] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page: page + 1, // Server pagination is one-based
        limit: rowsPerPage,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(actionType && { actionType }),
        ...(targetEntity && { targetEntity }),
      };

      const res = await API.get("/audit-logs", { params });

      setLogs(res.data.logs || []);
      setTotalCount(res.data.totalCount || 0);
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
      setError(err.response?.data?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, startDate, endDate, actionType, targetEntity]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setActionType("");
    setTargetEntity("");
    setPage(0);
    fetchLogs();
  };

  // Open modal when a row is clicked
  const handleRowClick = (log) => {
    setSelectedLog(log);
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedLog(null);
  };

  return (
    <div className="alpg-root">
      <style>{AUDIT_LOG_CSS}</style>

      {/* ----- Sticky top: title + filter bar ----- */}
      <div className="alpg-sticky-top">
        <div className="alpg-header">
          <div className="alpg-title-block">
            <div className="alpg-title">Audit Logs</div>
            <div className="alpg-subtitle">
              <span>Every recorded action across your organization</span>
              <span className="alpg-subtitle-dot" />
              <span className="alpg-count-pill">
                <b>{totalCount}</b> entries
              </span>
            </div>
          </div>
        </div>

        <div className="alpg-filterbar" role="search">
          <div className="alpg-filter-field">
            <label htmlFor="alpg-start-date">Start date</label>
            <input
              id="alpg-start-date"
              className="alpg-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="alpg-filter-field">
            <label htmlFor="alpg-end-date">End date</label>
            <input
              id="alpg-end-date"
              className="alpg-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="alpg-filter-field">
            <label htmlFor="alpg-action-type">Action type</label>
            <select
              id="alpg-action-type"
              className="alpg-select"
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
            >
              <option value="">All</option>
              <option value="REGISTER">REGISTER</option>
              <option value="REGISTER_FAIL">REGISTER_FAIL</option>
              <option value="VERIFY">VERIFY</option>
              <option value="VERIFY_FAIL">VERIFY_FAIL</option>
              <option value="LOGIN">LOGIN</option>
              <option value="LOGIN_FAIL">LOGIN_FAIL</option>
              <option value="LOGOUT">LOGOUT</option>
              <option value="UPDATE_PROFILE">UPDATE_PROFILE</option>
              <option value="CURRENCY_UPDATE">CURRENCY_UPDATE</option>
              {/* Add additional action types as needed */}
            </select>
          </div>
          <div className="alpg-filter-field">
            <label htmlFor="alpg-target-entity">Target entity</label>
            <input
              id="alpg-target-entity"
              className="alpg-input alpg-input-search"
              type="text"
              placeholder="e.g. User, Account…"
              value={targetEntity}
              onChange={(e) => setTargetEntity(e.target.value)}
            />
          </div>
          <div className="alpg-filter-actions">
            <button className="alpg-btn alpg-btn-primary" onClick={handleSearch}>
              <IconSearch />
              Search
            </button>
            <button
              className="alpg-btn alpg-btn-ghost"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* ----- Body ----- */}
      <div className="alpg-body">
        {error && (
          <div className="alpg-alert" role="alert">
            <span>{error}</span>
            <button onClick={() => setError("")} aria-label="Dismiss error">
              ×
            </button>
          </div>
        )}

        <div className="alpg-card">
          {loading ? (
            <div className="alpg-state alpg-state-loading">
              <CircularProgress size={24} />
              <div className="alpg-state-title">Loading audit logs…</div>
            </div>
          ) : (
            <>
              {logs.length === 0 ? (
                <div className="alpg-state">
                  <div className="alpg-state-icon">
                    <IconShield />
                  </div>
                  <div className="alpg-state-title">No audit logs found</div>
                  <div className="alpg-state-sub">
                    No entries match the current filters. Adjust the date range
                    or action type, or clear the filters to see everything.
                  </div>
                </div>
              ) : (
                <div className="alpg-table-wrap">
                  <table className="alpg-table">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Action Type</th>
                        <th>Target Entity</th>
                        <th>IP Address</th>
                        <th>Timestamp</th>
                        <th aria-label="View" />
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log._id} onClick={() => handleRowClick(log)}>
                          <td>
                            <span className="alpg-user-cell">
                              {logUsername(log)}
                            </span>
                          </td>
                          <td>
                            <ActionChip actionType={log.actionType} />
                          </td>
                          <td>
                            {log.targetEntity || (
                              <span className="alpg-muted">N/A</span>
                            )}
                          </td>
                          <td className="mono">
                            {log.ipAddress || (
                              <span className="alpg-muted">N/A</span>
                            )}
                          </td>
                          <td className="mono">{logTimestamp(log)}</td>
                          <td>
                            <span className="alpg-view-hint">
                              View details →
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="alpg-pagination-wrap">
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 25]}
                  count={totalCount}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Rows per page"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} of ${count}`
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* ----- Modal Dialog for Detailed View ----- */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        className="alpg-dlg"
        PaperProps={{ className: "alpg-root" }}
      >
        <style>{AUDIT_LOG_CSS}</style>
        <div className="alpg-dlg-head">
          <div>
            <div className="alpg-dlg-title">Audit Log Details</div>
            <div className="alpg-dlg-sub">
              Who did what, from where — with the exact before / after change.
            </div>
          </div>
          <button
            className="alpg-dlg-close"
            onClick={handleCloseModal}
            aria-label="close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="alpg-dlg-body">
          {selectedLog && (
            <div>
              <div className="alpg-detail-grid">
                <div>
                  <div className="alpg-detail-field-lab">Username</div>
                  <div className="alpg-detail-field-val">
                    {logUsername(selectedLog)}
                  </div>
                </div>
                <div>
                  <div className="alpg-detail-field-lab">Action Type</div>
                  <div className="alpg-detail-field-val">
                    <ActionChip actionType={selectedLog.actionType} />
                  </div>
                </div>
                <div>
                  <div className="alpg-detail-field-lab">Target Entity</div>
                  <div className="alpg-detail-field-val">
                    {selectedLog.targetEntity || "N/A"}
                  </div>
                </div>
                <div>
                  <div className="alpg-detail-field-lab">IP Address</div>
                  <div className="alpg-detail-field-val mono">
                    {selectedLog.ipAddress || "N/A"}
                  </div>
                </div>
                <div>
                  <div className="alpg-detail-field-lab">Timestamp</div>
                  <div className="alpg-detail-field-val mono">
                    {logTimestamp(selectedLog)}
                  </div>
                </div>
              </div>

              <div className="alpg-detail-sec-head">
                <span>Changes</span>
                <span className="alpg-detail-sec-hint">
                  before → after (word diff)
                </span>
              </div>
              <div className="alpg-diff-wrap">
                <ReactDiffViewer
                  oldValue={JSON.stringify(
                    safeObject(selectedLog.before),
                    null,
                    2
                  )}
                  newValue={JSON.stringify(
                    safeObject(selectedLog.after),
                    null,
                    2
                  )}
                  splitView={true}
                  compareMethod={DiffMethod.WORDS}
                  styles={{
                    diffViewer: {
                      fontFamily: "'Geist Mono', monospace",
                      fontSize: "12px",
                    },
                    diffRemoved: {
                      backgroundColor: "#ffeef0",
                    },
                    diffAdded: {
                      backgroundColor: "#e6ffed",
                    },
                  }}
                />
              </div>

              {selectedLog.error && (
                <div className="alpg-error-sec">
                  <div className="alpg-detail-sec-head">
                    <span>Error Details</span>
                  </div>
                  <pre className="alpg-error-pre">
                    {JSON.stringify(selectedLog.error, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <div className="alpg-dlg-foot">
          <span className="alpg-dlg-foot-l">
            Audit entries are read-only and cannot be edited.
          </span>
          <div className="alpg-dlg-foot-r">
            <button
              className="alpg-btn alpg-btn-primary"
              onClick={handleCloseModal}
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default AuditLogPage;
